<script lang="ts" setup>
import { onMounted, ref, computed } from 'vue'
import { storeToRefs } from 'pinia'
import { useTableStore } from '@/store/storeTable'
import { useCellSave } from '@/composables/useCellSave'
import { useUserPreferencesStore } from "@/store/storeUserPreferences.js"
import BaseInput from "@/components/BaseComponents/BaseInput.vue"
import BaseProgressBar from "@/components/BaseComponents/BaseProgressBar.vue"

const userPreferencesStore = useUserPreferencesStore()
const { userPreferences } = storeToRefs(userPreferencesStore)

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: null
  },
  readOnly: {
    type: Boolean,
    default: false
  },
  singleLine: {
    type: Boolean,
    default: false
  },
  autoSave: {
    type: Boolean,
    default: true
  },
  rowId: { type: [String, Number], default: '' },
  header: { type: Object, required: true },
  onSaveSuccess: { type: Function, default: null },
  // US 3399: valgfri id som settes paa selve kontrollen, saa en etikett kan knyttes med for=.
  // Uten den erklaeringen ville Vues attribute-fallthrough lagt id-en paa rot-diven, og en
  // for=-peker dit navngir ingenting for en skjermleser selv om statisk analyse blir fornoeyd.
  id: { type: String, default: undefined }
})

const emit = defineEmits(['update:modelValue'])
// Lokal valideringstilstand (prosentformat); lagringsfeil eies av useCellSave.
const hasError = ref(false)
const errorMessage = ref('')
const { saving, saveErrorMessage, saveCell } = useCellSave()

const inputError = computed(() => {
  if (hasError.value) return errorMessage.value.trim() || 'Feil'
  return saveErrorMessage.value
})
const tableStore = useTableStore()
const formattedModelValue = ref('')

function formatPercentValue (value: number | string | null): string {
  if (value === null || value === '') return ''
  const numValue = Number(value) * 100
  const fraction = props.header.numberFormatDecimalPrecision ?? 2;
  const decimalSep = userPreferences.value.numberFormatDecimalSeparator ?? ','
  return `${numValue.toFixed(fraction).replace('.', decimalSep)}%`
}

function parsePercentValue (value: string): number | null {
  const cleanValue = value.replace('%', '').replace(',', '.').trim()
  if (!isNumeric(cleanValue)) return null
  return Number(cleanValue) / 100
}

onMounted(async () => {
  await userPreferencesStore.getUserPreferences()
  formattedModelValue.value = formatPercentValue(props.modelValue)
})

async function changed (newValue: string) {
  const parsedValue = parsePercentValue(newValue)

  if (parsedValue === null) {
    hasError.value = true
    errorMessage.value = 'Ugyldig prosentformat. Bruk tall med opptil to desimaler.'
    formattedModelValue.value = newValue
    return
  }

  emit('update:modelValue', parsedValue)

  if (!props.autoSave) return

  const item = {
    tableKey: String(props.header?.tableKey || tableStore.tableKey || '').trim(),
    rowId: props.rowId,
    columnName: props.header.field,
    oldValue: props.modelValue,
    newValue: parsedValue
  }

  hasError.value = false
  errorMessage.value = ''
  const savedWithoutError = await saveCell(item, props.onSaveSuccess as (() => void | Promise<void>) | null);
  if (savedWithoutError) {
    formattedModelValue.value = formatPercentValue(parsedValue);
  }
}

function isNumeric (str: string): boolean {
  return !Number.isNaN(str as any) && !Number.isNaN(Number.parseFloat(str))
}
</script>

<template>
  <div>
    <template v-if="readOnly">
      <div :class="{ 'text-right w-full': singleLine }" class="p-2">
        <template v-if="modelValue != null && userPreferences">
          {{ formatPercentValue(modelValue) }}
        </template>
        <template v-else>
          <span class="italic text-fg-muted">(blank)</span>
        </template>
      </div>
    </template>
    <template v-else>
      <div class="w-full">
        <BaseInput :id="id" :error="inputError" :model-value="formattedModelValue" class="w-full" type="text"
                   @change="changed"/>
      </div>
      <BaseProgressBar v-if="saving"/>
      <div v-else class="h-1 m-0 p-0"/>
    </template>
  </div>
</template>
